import {
  AlertTriangle,
  CreditCard,
  Info,
  Lock,
  Users,
  UsersRoundIcon,
} from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/ui/sidebar';
import { MEDIA_URL } from '@/config';
import { useIsMobile } from '@/hooks/use-mobile';
import type { Team } from '@/types';

import { useTeamMembers } from '../../api/getTeamMembers';

import { DangerZoneView } from './danger-zone-view';
import { MembersView } from './members-view';
import { PasswordUpdateForm } from './password-update-form';
import { RestrictedView } from './restricted-view';
import { SubscriptionView } from './subscription-view';
import { TeamProfileForm } from './team-profile-form';

interface TeamSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialView?: string;
  activeTeam: Team;
  userId: string;
}

const navItems = [
  { name: 'Team Profile', icon: Info, id: 'team-profile' },
  { name: 'Password', icon: Lock, id: 'password' },
  { name: 'Members', icon: Users, id: 'members' },
  { name: 'Subscription', icon: CreditCard, id: 'subscription' },
  { name: 'Danger Zone', icon: AlertTriangle, id: 'danger-zone' },
] as const;

export type ViewId = (typeof navItems)[number]['id'];

export function TeamSettingsDialog({
  open,
  onOpenChange,
  initialView = 'team-profile',
  activeTeam,
  userId,
}: TeamSettingsDialogProps) {
  const [activeView, setActiveView] = useState<ViewId>(initialView as ViewId);
  const { data: members, isLoading: membersLoading } = useTeamMembers({
    teamId: activeTeam.id,
  });
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      if (open) setActiveView(initialView as ViewId);
    }
  }, [open, initialView]);

  const isCaptain =
    members?.some((m) => m.user.id === userId && m.captain) ?? false;

  const renderView = () => {
    if (membersLoading) return <div className="p-6">Loading members...</div>;

    const views: Record<ViewId, JSX.Element> = {
      'team-profile': isCaptain ? (
        <TeamProfileForm team={activeTeam} />
      ) : (
        <RestrictedView />
      ),
      password: isCaptain ? (
        <PasswordUpdateForm teamId={activeTeam.id} />
      ) : (
        <RestrictedView />
      ),
      members: (
        <MembersView
          teamId={activeTeam.id}
          userId={userId}
          teamName={activeTeam.team_name}
          isCaptain={isCaptain}
        />
      ),
      subscription: (
        <SubscriptionView teamId={activeTeam.id} isCaptain={isCaptain} />
      ),
      'danger-zone': isCaptain ? (
        <DangerZoneView
          team={activeTeam}
          onOpenChange={onOpenChange}
          setActiveView={setActiveView}
        />
      ) : (
        <RestrictedView />
      ),
    };

    return views[activeView];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-h-[min(640px,80vh)] md:max-w-[800px]">
        <DialogTitle className="sr-only">Team Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage your team settings here.
        </DialogDescription>
        <SidebarProvider>
          <Sidebar collapsible="none" className="hidden w-56 md:flex">
            <SidebarHeader>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    size="lg"
                    className="bg-transparent!"
                    asChild
                  >
                    <div>
                      <Avatar className="size-8">
                        <AvatarImage
                          src={`${MEDIA_URL}/images/uploads/${activeTeam.team_photo}`}
                          alt={activeTeam.team_name}
                          className="rounded-lg"
                        />
                        <AvatarFallback className="rounded-lg">
                          <UsersRoundIcon
                            size={16}
                            className="opacity-60"
                            aria-hidden="true"
                          />
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold">
                          {activeTeam.team_name}
                        </span>
                        <span className="truncate text-xs">
                          {activeTeam.isPremium ? 'Subcription' : 'Free'}
                        </span>
                      </div>
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {navItems.map((item) => (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          asChild
                          isActive={activeView === item.id}
                          onClick={() => setActiveView(item.id)}
                        >
                          <a href="/" onClick={(e) => e.preventDefault()}>
                            <item.icon />
                            <span>{item.name}</span>
                          </a>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
          <main className="flex flex-1 flex-col sm:max-h-[min(640px,80vh)]">
            {isMobile && (
              <div className="border-b p-4">
                <div className="mb-4 flex items-center gap-3">
                  <Avatar className="size-8">
                    <AvatarImage
                      src={`${MEDIA_URL}/images/uploads/${activeTeam.team_photo}`}
                      alt={activeTeam.team_name}
                      className="rounded-lg"
                    />
                    <AvatarFallback className="rounded-lg">
                      <UsersRoundIcon
                        size={16}
                        className="opacity-60"
                        aria-hidden="true"
                      />
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {activeTeam.team_name}
                    </span>
                    <span className="truncate text-xs">
                      {activeTeam.isPremium ? 'Subcription' : 'Free'}
                    </span>
                  </div>
                </div>
                <Select
                  value={activeView}
                  onValueChange={(value) => setActiveView(value as ViewId)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a view" />
                  </SelectTrigger>
                  <SelectContent>
                    {navItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        <div className="flex items-center gap-2">
                          <item.icon className="h-4 w-4" />
                          <span>{item.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {renderView()}
          </main>
        </SidebarProvider>
      </DialogContent>
    </Dialog>
  );
}
